import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  Eye,
  FileSearch,
  LayoutDashboard,
  Megaphone,
  MousePointerClick,
  Store,
  Trophy,
} from 'lucide-react';
import {
  useSeoAgentsQuery,
  useSeoAiAnswersQuery,
  useSeoCompetitorsQuery,
  useSeoHealthQuery,
  useSeoKeywordsQuery,
  useSeoSearchConsoleQuery,
  mainGscProperty,
} from '@/client/api/marketing/seo';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  BAD_SERIES,
  NEUTRAL_SERIES,
  SITE_COLOR,
  SITE_LABEL,
  SeoKpi,
  SeoNote,
  SeoPanel,
  SeoTrend,
  ShareBar,
  fmtNumber,
} from './components/seo-ui';

const CHECK_ACTION: Record<string, string> = {
  status_200: 'pages in the sitemap do not return 200',
  canonical_self: 'pages point their canonical at another URL',
  indexable: 'pages are marked noindex',
  title_present: 'pages have no title',
  title_specific: 'pages fall back to a generic title',
  jsonld_type: 'pages are missing their structured data',
  jsonld_valid: 'pages carry structured data that does not parse',
};

function PanelLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900">
      {label}
      <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

/**
 * `/admin/seo` — where we stand on every SEO measure at once, and what to fix
 * next. Every number here is the headline of one of the pages below it.
 */
export default function SeoOverviewPage() {
  const health = useSeoHealthQuery();
  const keywords = useSeoKeywordsQuery();
  const answers = useSeoAiAnswersQuery();
  const competitors = useSeoCompetitorsQuery(90);
  const agents = useSeoAgentsQuery(30);
  const gsc = useSeoSearchConsoleQuery();
  const gp = mainGscProperty(gsc.data);
  const gt = gp?.totals;
  const gb = gp?.previous_totals;
  const change = (now?: number, before?: number) =>
    now != null && before ? `${now >= before ? '+' : ''}${Math.round(((now - before) / before) * 100)}% on last week's pull` : undefined;

  const hs = health.data?.run?.summary;
  const ks = keywords.data?.summary;
  const as = answers.data?.summary;
  const latest = competitors.data?.latest;
  const jiji = latest?.sources.find((s) => s.source === 'jiji');

  // What to do next, read straight off the data: the checks failing on most
  // sampled pages, the biggest searches we do not rank for, the questions no
  // assistant answers with us.
  const failing = Object.entries(hs?.failing_checks ?? {})
    .filter(([id]) => CHECK_ACTION[id])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  const missed = (keywords.data?.keywords ?? [])
    .filter((k) => k.positions && k.positions.feelyfeely == null && (k.volume ?? 0) > 0)
    .slice(0, 5);
  const unnamed = (answers.data?.prompts ?? []).filter(
    (p) => Object.keys(p.answers).length > 0 && !Object.values(p.answers).some((a) => a?.names_us),
  );
  const trend = (health.data?.history ?? []).map((h) => ({ day: h.at.slice(0, 10), fail: h.summary.fail }));

  return (
    <MarketingPageShell
      title="SEO overview"
      description="Where we stand with search engines and AI assistants, against Jiji and Meqasa, and what to fix next."
      crumbs={[{ label: 'SEO' }, { label: 'Overview' }]}
      icon={LayoutDashboard}
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SeoKpi
            label="Clicks from Google"
            value={gt?.clicks}
            icon={MousePointerClick}
            hint={change(gt?.clicks, gb?.clicks) ?? 'Last 28 days, Search Console'}
            to="/admin/seo/search-console"
            loading={gsc.isLoading}
          />
          <SeoKpi
            label="Shown in Google results"
            value={gt?.impressions}
            icon={Eye}
            hint={change(gt?.impressions, gb?.impressions) ?? 'Impressions, last 28 days'}
            to="/admin/seo/search-console"
            loading={gsc.isLoading}
          />
          <SeoKpi
            label="Average Google position"
            value={gt?.position ?? undefined}
            icon={Trophy}
            hint={gt ? `Across ${fmtNumber(gt.queries)} searches we appeared for` : undefined}
            to="/admin/seo/search-console"
            loading={gsc.isLoading}
          />
          <SeoKpi
            label="Pages failing health checks"
            value={hs ? `${hs.fail} of ${hs.pages}` : undefined}
            icon={FileSearch}
            tone={hs ? (hs.fail > 0 ? 'bad' : 'good') : 'neutral'}
            hint="Sampled nightly from the sitemaps"
            to="/admin/seo/health"
            loading={health.isLoading}
          />
          <SeoKpi
            label="Keywords on Google's first page"
            value={ks ? `${ks.top10.feelyfeely} of ${ks.checked}` : undefined}
            icon={Trophy}
            tone={ks && ks.checked > 0 && ks.top10.feelyfeely === 0 ? 'bad' : 'neutral'}
            hint={ks ? `Jiji ${ks.top10.jiji}, Meqasa ${ks.top10.meqasa}` : undefined}
            to="/admin/seo/keywords"
            loading={keywords.isLoading}
          />
          <SeoKpi
            label="AI answers that name us"
            value={as ? `${as.share.feelyfeely ?? 0}%` : undefined}
            icon={Megaphone}
            tone={as && as.answers > 0 && !as.share.feelyfeely ? 'bad' : 'neutral'}
            hint={as ? `Jiji is named in ${as.share.jiji ?? 0}%` : undefined}
            to="/admin/seo/ai-answers"
            loading={answers.isLoading}
          />
          <SeoKpi
            label="Our listings"
            value={latest?.ours.total}
            icon={Store}
            hint={jiji ? `Jiji: ${fmtNumber(jiji.live)} seen in ${latest?.live_days} days` : undefined}
            to="/admin/seo/competitors"
            loading={competitors.isLoading}
          />
          <SeoKpi
            label="Pages read by AI crawlers"
            value={agents.data?.totals.ai}
            icon={Bot}
            hint="Last 30 days"
            to="/admin/seo/agents"
            loading={agents.isLoading}
          />
          <SeoKpi
            label="Visits from AI assistants"
            value={agents.data?.referrals.visits}
            icon={MousePointerClick}
            hint="Last 30 days"
            to="/admin/seo/agents"
            loading={agents.isLoading}
          />
        </div>

        <SeoPanel title="What to fix next" subtitle="Read off the latest checks, largest first">
          {failing.length + missed.length + unnamed.length === 0 ? (
            <p className="py-2 text-sm text-gray-600">
              Nothing outstanding in the latest checks, or the checks have not run yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 text-sm">
              {failing.map(([id, count]) => (
                <li key={id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-gray-900">
                    <span className="font-semibold tabular-nums">{count}</span> sampled {CHECK_ACTION[id]}
                  </span>
                  <PanelLink to="/admin/seo/health" label="See the pages" />
                </li>
              ))}
              {missed.length > 0 && (
                <li className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-gray-900">
                    Not in Google's top 100 for{' '}
                    {missed.map((k, i) => (
                      <span key={k.keyword}>
                        {i > 0 && ', '}
                        <span className="font-medium">{k.keyword}</span>{' '}
                        <span className="text-gray-500">({fmtNumber(k.volume)}/mo)</span>
                      </span>
                    ))}
                  </span>
                  <PanelLink to="/admin/seo/keywords" label="See keywords" />
                </li>
              )}
              {unnamed.length > 0 && (
                <li className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-gray-900">
                    <span className="font-semibold tabular-nums">{unnamed.length}</span> of{' '}
                    {answers.data?.prompts.length} buyer questions get an answer that never names us
                  </span>
                  <PanelLink to="/admin/seo/ai-answers" label="Read the answers" />
                </li>
              )}
            </ul>
          )}
        </SeoPanel>

        <div className="grid gap-4 lg:grid-cols-3">
          <SeoPanel
            title="Google first page"
            subtitle={ks ? `Share of ${ks.checked} checked keywords in the top 10` : 'Share of checked keywords in the top 10'}
            action={<PanelLink to="/admin/seo/keywords" label="Keywords" />}
          >
            {ks && ks.checked > 0 ? (
              (keywords.data?.sites ?? []).map((site) => (
                <ShareBar
                  key={site}
                  label={SITE_LABEL[site]}
                  value={Math.round((ks.top10[site] / ks.checked) * 100)}
                  color={SITE_COLOR[site]}
                  emphasis={site === 'feelyfeely'}
                />
              ))
            ) : (
              <p className="py-2 text-sm text-gray-600">No rankings checked yet.</p>
            )}
          </SeoPanel>

          <SeoPanel
            title="AI assistants"
            subtitle={as ? `Share of ${as.answers} answers naming each` : 'Share of answers naming each'}
            action={<PanelLink to="/admin/seo/ai-answers" label="AI answers" />}
          >
            {as && as.answers > 0 ? (
              [...(answers.data?.brands ?? [])]
                .sort((a, b) => (as.share[b] ?? 0) - (as.share[a] ?? 0))
                .map((brand) => (
                  <ShareBar
                    key={brand}
                    label={SITE_LABEL[brand] ?? brand}
                    value={as.share[brand] ?? 0}
                    color={SITE_COLOR[brand] ?? NEUTRAL_SERIES}
                    emphasis={brand === 'feelyfeely'}
                  />
                ))
            ) : (
              <p className="py-2 text-sm text-gray-600">The assistants have not been asked yet.</p>
            )}
          </SeoPanel>

          <SeoPanel
            title="Failing pages per check"
            subtitle="Nightly page health; lower is better"
            action={<PanelLink to="/admin/seo/health" label="Page health" />}
          >
            <SeoTrend
              small
              height={150}
              data={trend}
              series={[{ key: 'fail', name: 'Failing pages', color: BAD_SERIES, kind: 'bar' }]}
            />
          </SeoPanel>
        </div>

        <SeoNote>
          Page health runs nightly, the competitor reading nightly, keywords and AI answers weekly. Each tile opens the
          page behind its number.
        </SeoNote>
      </div>
    </MarketingPageShell>
  );
}
